import React, { useCallback, useEffect } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';

interface ImageViewerModalProps {
  open: boolean;
  onClose: () => void;
  images: Array<{ id: string; imagePath: string; imageName?: string; templateName?: string; createdAt?: string }>;
  currentIndex: number;
  offerId: string;
  onIndexChange: (index: number) => void;
}

export default function ImageViewerModal({
  open,
  onClose,
  images,
  currentIndex,
  offerId,
  onIndexChange,
}: ImageViewerModalProps) {
  const currentImage = images[currentIndex];
  const imageUrl = currentImage?.imagePath || '';
  const imageName = currentImage?.imageName;

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      onIndexChange(currentIndex + 1);
    }
  }, [currentIndex, images.length, onIndexChange]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      } else if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handlePrevious, handleNext, onClose]);

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < images.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6">
              Image {currentIndex + 1} of {images.length} for Offer {offerId}
            </Typography>
            {imageName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {imageName}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Image Container */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            bgcolor: 'grey.900',
            overflow: 'hidden',
          }}
        >
          {/* Previous Button */}
          <IconButton
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            sx={{
              position: 'absolute',
              left: 16,
              zIndex: 1,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(0, 0, 0, 0.2)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
            aria-label="previous image"
          >
            <ChevronLeftIcon />
          </IconButton>

          {/* Image */}
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={`Image ${currentIndex + 1} for offer ${offerId}`}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Typography variant="body1" color="text.secondary">
              No image available
            </Typography>
          )}

          {/* Next Button */}
          <IconButton
            onClick={handleNext}
            disabled={!canGoNext}
            sx={{
              position: 'absolute',
              right: 16,
              zIndex: 1,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(0, 0, 0, 0.2)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
            aria-label="next image"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* Footer Navigation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button variant="outlined" startIcon={<ChevronLeftIcon />} onClick={handlePrevious} disabled={!canGoPrevious}>
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            {currentIndex + 1} / {images.length}
          </Typography>
          <Button variant="outlined" endIcon={<ChevronRightIcon />} onClick={handleNext} disabled={!canGoNext}>
            Next
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
